import React, { useState, useEffect, useRef } from 'react';
import { CiMicrophoneOff } from "react-icons/ci";
import { CiMicrophoneOn } from "react-icons/ci";
import '../chatbox.css'
import { errorToast } from '../../_helper/helper';

export default function VoiceAssistant({
  apiUrl,
  setInputValue
}) {
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [isRecording, setIsRecording] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);
  const [isTranscriptVisible, setIsTranscriptVisible] = useState(false);

  const wsRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const captureBufferRef = useRef(new Float32Array(0));
  const recorderTimeoutRef = useRef(null);
  const sendAudioIntervalRef = useRef(null); // For throttling audio send interval

  const SAMPLE_RATE = 16000;
  const CHANNELS = 1;
  const BUFFER_SIZE = 1024;
  const CHUNK_DURATION_MS = 200;

  useEffect(() => {
    return () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        console.log("Closing WebSocket connection.");
        wsRef.current.close();
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      if (sendAudioIntervalRef.current) {
        clearInterval(sendAudioIntervalRef.current); // Clear the interval when the component is cleaned up
      }
    };
  }, []);

  const initializeWebSocket = (url) => {
    wsRef.current = new WebSocket(url);
    wsRef.current.binaryType = 'arraybuffer';

    wsRef.current.onopen = () => {
      console.log('WebSocket connection opened.');
      setConnectionStatus('connected');
    };

    wsRef.current.onmessage = (event) => {
      // console.log('Received message:', event.data);
      const data = JSON.parse(event.data);
      if (data.text) {
        if (data.type === 'final') {
          // Set the final transcript to both state and call setInputValue
          setFinalTranscript((prev) => prev + data.text + ' ');
          console.log('Final transcript:', finalTranscript);
          
          // Call the function to update input value with final transcript
          setInputValue(data.text);  // Pass the final response to setInputValue
        } else if (data.type === 'partial') {
          setInterimTranscript(data.text);
          setInputValue(data.text);
          console.log('Interim transcript:', interimTranscript);
        }
      }
    };

    wsRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
      setConnectionStatus('disconnected');
    };

    wsRef.current.onclose = () => {
      console.log('WebSocket connection closed.');
      setConnectionStatus('disconnected');
    };
  };

  const startAudioStreaming = async () => {
    setConnectionStatus('connecting');
    console.log('Connecting to WebSocket for audio streaming...');

    try {
      // Check microphone permissions
      const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
      if (permissionStatus.state === 'denied') {
        console.error('Microphone access denied. Please enable in browser settings.');
        errorToast('Microphone access denied. Please enable in browser settings.');
        return;
      }

      // Acquire microphone
      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: CHANNELS,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: SAMPLE_RATE
        },
        video: false
      });

      // Initialize AudioContext
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: SAMPLE_RATE
      });

      // Initialize WebSocket
      const wsUrl = `wss${apiUrl}/stt/transcribe`;
      initializeWebSocket(wsUrl);

      // Process audio input
      const processor = audioContextRef.current.createScriptProcessor(BUFFER_SIZE, CHANNELS, CHANNELS);
      const source = audioContextRef.current.createMediaStreamSource(mediaStreamRef.current);
      source.connect(processor);
      processor.connect(audioContextRef.current.destination);

      processor.onaudioprocess = (e) => {
        const inputData = e.inputBuffer.getChannelData(0);
        let sum = 0;
        for (let i = 0; i < inputData.length; i++) sum += Math.abs(inputData[i]);
        const avg = sum / inputData.length;
        const level = Math.min(100, Math.floor(avg * 1000));
        setAudioLevel(level);

        // Append to captureBuffer
        const newBuffer = new Float32Array(captureBufferRef.current.length + inputData.length);
        newBuffer.set(captureBufferRef.current, 0);
        newBuffer.set(inputData, captureBufferRef.current.length);
        captureBufferRef.current = newBuffer;
      };

      // Throttle the sending of audio data at fixed intervals
      sendAudioIntervalRef.current = setInterval(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && captureBufferRef.current.length > 0) {
          const pcmBuffer = new Int16Array(floatTo16BitPCM(captureBufferRef.current));
          try {
            wsRef.current.send(pcmBuffer);
            // console.log('Sent audio buffer:', pcmBuffer);
            captureBufferRef.current = new Float32Array(0); // Clear the buffer after sending
          } catch (e) {
            console.error('Failed to send audio chunk:', e);
          }
        }
      }, CHUNK_DURATION_MS); // Adjust the interval as necessary

      setIsRecording(true);
      setIsTranscriptVisible(true);
      setInputValue("Listing...")
    } catch (error) {
      console.error('Error starting audio streaming:', error);
      setConnectionStatus('disconnected');
      errorToast('Mircophone not found, error starting audio streaming');
    }
  };

  const stopAudioStreaming = () => {
    setIsRecording(false);
    setIsTranscriptVisible(false);

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
      console.log('WebSocket connection closed.');
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    captureBufferRef.current = new Float32Array(0);
    setFinalTranscript('');
    setInterimTranscript('');
    console.log('Stopped audio streaming.');
    
    if (sendAudioIntervalRef.current) {
      clearInterval(sendAudioIntervalRef.current); // Clear the interval when stopping
    }
  };

  const floatTo16BitPCM = (float32Array) => {
    const len = float32Array.length;
    const buffer = new ArrayBuffer(len * 2);
    const view = new DataView(buffer);
    let offset = 0;
    for (let i = 0; i < len; i++, offset += 2) {
      let s = Math.max(-1, Math.min(1, float32Array[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return buffer;
  };

  const handleMicClick = () => {
    if (isRecording) {
      stopAudioStreaming();
    } else {
      startAudioStreaming();
    }
  };

  const micStyle = {
    transform: `scale(${1 + audioLevel / 70})`,
    // opacity: audioLevel / 100,
    transition: 'transform 0.1s ease-out, opacity 0.1s ease-out',
    color:'green', 
  };

  return (
    <div>
      {/* <div>
        Connection Status: {connectionStatus}
      </div>
      <div>
        {isTranscriptVisible && (
          <div>
            <strong>Final Transcript:</strong> {finalTranscript}
            <br />
            <strong>Interim Transcript:</strong> {interimTranscript}
          </div>
        )}
      </div>
      <div>
        <button onClick={handleMicClick}>
          {isRecording ? 'Stop Recording' : 'Start Recording'}
        </button>
      </div> */}

      {/* Microphone icons with animation based on recording and audio level */}
      <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '10px', borderRadius: '50%', backgroundColor: micStyle.backgroundColor }}>
        {isRecording ? ( 
            <CiMicrophoneOn size={30} className="mic-animation" style={micStyle} onClick={handleMicClick} />
        ) : (
          <CiMicrophoneOff size={30} style={{ color: 'red' }} onClick={handleMicClick} />
        )}
      </div>
    </div>
  );
}
